import * as React from "react";
import * as ReactQuery from "react-query";
import { UseInfiniteQueryResult } from "react-query";
import axios from "axios";
import { useGtm } from "../../../Common";


import { RequestProductsSearch, ResponseProductsSearch } from "../../model";

import {
  PlpDataSourceProductList_ProductListQueryKeyType,
  PlpDataSourceProductList_QueryKey,
} from "./plp-data-source-product-list-query-key-factory";

export interface PlpDataSourceProductListQueryConsumerInterface {
  isAttached?: boolean;
  data: UseInfiniteQueryResult<ResponseProductsSearch>["data"];
  status: UseInfiniteQueryResult<ResponseProductsSearch>["status"];
  fetchNextPage: UseInfiniteQueryResult<ResponseProductsSearch>["fetchNextPage"];
  fetchPreviousPage: UseInfiniteQueryResult<ResponseProductsSearch>["fetchNextPage"];
  hasNextPage: UseInfiniteQueryResult<ResponseProductsSearch>["hasNextPage"];
  hasPreviousPage: UseInfiniteQueryResult<ResponseProductsSearch>["hasNextPage"];
  isFetchingNextPage: UseInfiniteQueryResult<ResponseProductsSearch>["isFetchingNextPage"];
  isFetchingPreviousPage: UseInfiniteQueryResult<ResponseProductsSearch>["isFetchingPreviousPage"];
}

const defaultValue =
  {} as unknown as PlpDataSourceProductListQueryConsumerInterface;

const PlpDataSourceProductListQueryContext = React.createContext(defaultValue);

export interface PlpDataSourceProductListQueryProviderProps {
  desiredProductList: RequestProductsSearch;
}

type PlpDataSourceProductList_QueryPageParam = number;

export const PlpDataSourceProductListQueryProvider: React.FunctionComponent<PlpDataSourceProductListQueryProviderProps> = (props) => {
  const { pushToTheDataLayer } = useGtm();

  const queryKey = React.useMemo(() => {
    return PlpDataSourceProductList_QueryKey.productList(
      props.desiredProductList
    );
  }, [props.desiredProductList]);

  const queryFn: (context: QueryFunctionContext<PlpDataSourceProductList_ProductListQueryKeyType, PlpDataSourceProductList_QueryPageParam>) => Promise<T> = async (
    context: ReactQuery.QueryFunctionContext<
      PlpDataSourceProductList_ProductListQueryKeyType,
      PlpDataSourceProductList_QueryPageParam
    >
  ) => {
    const [baseKey, request] = context.queryKey;

    const { options: request_options, ...request_base } = request;

    const offset = context.pageParam ?? props.desiredProductList.offset;

    const channel = props.desiredProductList.channel;

    const queryData = {
      ...request_base,
      ...request_options,
      ...{
        offset,
        channel,
      },
    };

    const { data } = await axios.get<ResponseProductsSearch>(
      "/datasource/fetch/productlist",
      {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        params: queryData,
      }
    );

    if (queryData.search) {
      pushToTheDataLayer({event: "search", search_term: queryData.search});
    }

    return data;
  };

  const getNextPageParam: ReactQuery.GetNextPageParamFunction<
    ResponseProductsSearch
  > = (lastPage) => {
    const lastCursor = lastPage.cursor;

    const lastPageSize = lastPage.products.length;

    const cursorAtTheBottomOfLastPage = lastCursor + lastPageSize;

    const maxCursor = lastPage?.results_total || 0;

    if (cursorAtTheBottomOfLastPage >= maxCursor) {
      return undefined;
    }

    return cursorAtTheBottomOfLastPage;

    /**
     * Had to make a backend modification to support returning a cursor,
     * but if you had to do this without the cursor in the response,
     * you basically do this:
     */
    /*const howManyProductsDoICurrentlyHaveInMyCacheOfAllThePages = (
      allPages || []
    ).reduce((acc, page) => acc + page.products.length, 0);

    const howManyProductsTotal = lastPage?.results_total || 0;

    if (
      howManyProductsDoICurrentlyHaveInMyCacheOfAllThePages >=
      howManyProductsTotal
    ) {
      return undefined;
    }

    return howManyProductsDoICurrentlyHaveInMyCacheOfAllThePages;*/
  };

  const getPreviousPageParam: ReactQuery.GetPreviousPageParamFunction<
    ResponseProductsSearch
  > = (firstPage) => {
    const firstPageCursor = firstPage.cursor;

    if (firstPageCursor <= 0) {
      return undefined;
    }

    const cursorAtTheBottomOfTheNewPreviousPage = firstPageCursor;

    const anyPageSize = firstPage.cursor_page_size;

    const cursorAtTheTopOfTheNewPreviousPage = Math.max(
      cursorAtTheBottomOfTheNewPreviousPage - anyPageSize,
      0
    );

    return cursorAtTheTopOfTheNewPreviousPage;
  };

  const {
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    status,
    data,
    ...result
  } = ReactQuery.useInfiniteQuery(queryKey, queryFn, {
    getNextPageParam,
    getPreviousPageParam: getPreviousPageParam,
  });

  const value: PlpDataSourceProductListQueryConsumerInterface = React.useMemo(
    () => ({
      isAttached: true,
      data,
      status,
      fetchNextPage,
      fetchPreviousPage,
      hasNextPage,
      hasPreviousPage,
      isFetchingNextPage,
      isFetchingPreviousPage,
    }),
    [
      data,
      status,
      fetchNextPage,
      fetchPreviousPage,
      hasNextPage,
      hasPreviousPage,
      isFetchingNextPage,
      isFetchingPreviousPage,
    ]
  );
  return (
    <PlpDataSourceProductListQueryContext.Provider value={value}>
      {props?.children || []}
    </PlpDataSourceProductListQueryContext.Provider>
  );
};

export const usePlpDataSourceProductListQuery = () => {
  const context = React.useContext(PlpDataSourceProductListQueryContext);

  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (!context || context === defaultValue) {
  //   throw new Error(
  //     "usePlpDataSourceProductListQuery must be used within a PlpDataSourceProductListQueryProvider"
  //   );
  // }

  return context;
};
